import { api } from "@/integrations/clients/carAuction/carAuctionClient";

/**
 * Admin stats API – UI uses useAdminStats hook which calls this service.
 * Response DTOs match backend GetAdminStatsResponse / GetAdminStatsTimeSeriesResponse,
 * so no mapping is needed in the frontend.
 */
export type StatsPeriod = "1d" | "1w" | "1m" | "all" | "custom";

export interface AdminStatsUsers {
  onlineCount: number;
  newInPeriod: number;
  deletedInPeriod: number;
  pendingRequests: number;
}

export interface AdminStatsLeads {
  incomingInPeriod: number;
  fromGuestsInPeriod: number;
  pending: number;
}

export interface AdminStatsCarTests {
  incomingInPeriod: number;
  approvedInPeriod: number;
  rejectedInPeriod: number;
  ledToListingsInPeriod: number;
  pendingReview: number;
}

export interface LatestBidItem {
  id: string;
  amount: number;
  listingTitle: string;
  createdAt: string;
}

export interface AdminStatsBids {
  latestBids: LatestBidItem[];
  bidCountInPeriod: number;
  uniqueBiddersInPeriod: number;
}

/** Single row for admin latest bids list (paginated). */
export interface AdminLatestBidItem {
  id: string;
  amount: number;
  isHighestBid: boolean;
  bidderName: string;
  bidderEmail: string;
  listingTitle: string;
  registrationNo: string;
  createdAt: string;
}

export interface GetAdminLatestBidsResponse {
  items: AdminLatestBidItem[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminStatsListings {
  purchasedDirectInPeriod: number;
  purchasedAuctionInPeriod: number;
  newInPeriod: number;
  cancelledInPeriod: number;
  active: number;
  draft: number;
  endingWithin24h: number;
  auctionsWithBids: number;
  endedInPeriod: number;
}

export interface AdminStatsResponse {
  period: StatsPeriod;
  fromDate?: string;
  toDate?: string;
  users: AdminStatsUsers;
  leads: AdminStatsLeads;
  carTests: AdminStatsCarTests;
  bids: AdminStatsBids;
  listings: AdminStatsListings;
}

export interface TimeSeriesPoint {
  date: string;
  count: number;
}

export type StatsTimeSeriesInterval = "day" | "week" | "month";

export interface AdminStatsTimeSeriesResponse {
  period: StatsPeriod;
  interval: StatsTimeSeriesInterval;
  leads: TimeSeriesPoint[];
  carTests: TimeSeriesPoint[];
  bids: TimeSeriesPoint[];
  listings: TimeSeriesPoint[];
}

const INTERVAL_LABELS: Record<StatsTimeSeriesInterval, string> = {
  day: "Per dygn",
  week: "Per vecka",
  month: "Per månad",
};

export function getIntervalLabel(interval: StatsTimeSeriesInterval): string {
  return INTERVAL_LABELS[interval] ?? interval;
}

const PERIOD_LABELS: Record<StatsPeriod, string> = {
  "1d": "Senaste 24 h",
  "1w": "Senaste 7 dagar",
  "1m": "Senaste 30 dagar",
  all: "Senaste året",
  custom: "Specifik period",
};

export function getPeriodLabel(period: StatsPeriod): string {
  return PERIOD_LABELS[period] ?? period;
}

export interface AdminStatsDateRange {
  from: string; // YYYY-MM-DD
  to: string;
}

export class AdminStatsService {
  static async getStats(
    period: StatsPeriod = "1w",
    dateRange?: AdminStatsDateRange
  ): Promise<AdminStatsResponse> {
    const params: Record<string, string> = { period };
    if (period === "custom" && dateRange?.from && dateRange?.to) {
      params.fromDate = dateRange.from;
      params.toDate = dateRange.to;
    }
    const body = await api.get<{ success: boolean; data: AdminStatsResponse }>(
      "/admin/stats",
      { params }
    );
    return (body as { success: boolean; data: AdminStatsResponse }).data;
  }

  static async getTimeSeries(
    period: StatsPeriod = "1w",
    interval: StatsTimeSeriesInterval = "day",
    dateRange?: AdminStatsDateRange
  ): Promise<AdminStatsTimeSeriesResponse> {
    const params: Record<string, string> = { period, interval };
    if (period === "custom" && dateRange?.from && dateRange?.to) {
      params.fromDate = dateRange.from;
      params.toDate = dateRange.to;
    }
    const body = await api.get<{
      success: boolean;
      data: AdminStatsTimeSeriesResponse;
    }>("/admin/stats/time-series", { params });
    return (body as { success: boolean; data: AdminStatsTimeSeriesResponse }).data;
  }

  static async getLatestBids(
    page: number = 1,
    limit: number = 10
  ): Promise<GetAdminLatestBidsResponse> {
    const body = await api.get<{
      success: boolean;
      data: GetAdminLatestBidsResponse;
    }>("/admin/stats/bids/latest", { params: { page, limit } });
    return (body as { success: boolean; data: GetAdminLatestBidsResponse }).data;
  }
}
